import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  CardMedia,
  Paper,
  Chip,
  Switch,
  Stack,
  Avatar,
  Tabs,
  Tab,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  Chat as ChatIcon,
  LocationOn as LocationIcon,
  ArrowBackIos,
  ArrowForwardIos,
  Lock,
  CalendarToday as CalendarTodayIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { request } from '../../Utils/request';
import COLORS from '../../Utils/colors';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { paiseToRupees } from '../../Utils/currencyConverter';
import { getOrCreateChat } from '../../services/CommonApiCall';

interface IFile {
  fileKey: string;
  fileType: string;
  featured: boolean;
  fileURL?: string;
}

interface ITask {
  placeRefId?: string;
  name: string;
  file?: IFile;
  description: string;
}

interface IDailySchedule {
  day: string;
  tasks: ITask[];
}

interface ISchedule {
  dailySchedule: IDailySchedule[];
}

interface ILocation {
  lat: number;
  lng: number;
  name: string;
}

interface IUser {
  _id: string;
  id?: string;
  firstName: string;
  lastName: string;
  username: string;
}

interface IBatch {
  _id?: string;
  from: string;
  to: string;
  groupSize?: number;
  maxGroupSizeAllowed?: number;
}

interface IUserBooking {
  _id: string;
  bookingId: string;
  status: 'confirmed' | 'cancelled' | 'pending';
  batchData: {
    from: string;
    to: string;
  };
  quantity: number;
  userInfo: any[];
}

export interface ITripDetail {
  _id: string;
  title: string;
  description: string;
  pricePerPerson: number;
  schedule: ISchedule;
  notes: string;
  category: string[];
  location: ILocation;
  files: IFile[];
  userId: IUser;
  type: 'Trip' | 'Itinerary';
  status?: 'active' | 'inactive';
  user: IUser;
  batches?: IBatch[];
}

const TripDetail: React.FC = () => {
  const navigate = useNavigate();
  const locationParams = useLocation();
  const { id } = useParams<{ id: string }>();
  const user = useSelector((state: RootState) => state.user);
  
  // Get type from navigation state, query params, or default to 'trip'
  const searchParams = new URLSearchParams(locationParams.search);
  const type = locationParams?.state?.type || 
               searchParams.get('type') || 
               'trip';
  const paramsID = id;

  // Check if phone is verified
  const isPhoneVerified = user?.user?.phone?.isVerified === true;
  const [trip, setTrip] = useState<ITripDetail | null>(null);
  const [selectedDayIdx, setSelectedDayIdx] = useState(0);
  const [active, setActive] = useState<boolean>(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const currentUser = useSelector((state: RootState) => state.user.user);
  const [userBooking, setUserBooking] = useState<IUserBooking | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelBatchDialogOpen, setCancelBatchDialogOpen] = useState(false);
  const [selectedBatchToCancel, setSelectedBatchToCancel] = useState<string | null>(null);
  const [isCancellingBatch, setIsCancellingBatch] = useState(false);

  useEffect(() => {
    if (!paramsID) return;

    const fetchTrip = async () => {
      if (type === 'itinerary') {
        const res = await request<{ success: boolean; data: ITripDetail; message: string }>(
          `/itineraries/${paramsID}`,
          {
            method: 'GET',
            authorization:true
          }
        ).then((res) => {
          setTrip(res.data);
          setActive(res.data?.status !== 'inactive');
        }).catch((err) => {
          console.error(err);
        });
      } else {
        const res = await request<{ success: boolean; data: ITripDetail; message: string }>(
          `/trips/${paramsID}`,
          {
            method: 'GET',
            authorization: true,
          }
        ).then((res) => {
          setTrip(res.data);
          setActive(res.data?.status !== 'inactive');
        }).catch((err) => {
          console.error(err);
        });
      }
      // try {
      //   const res = await request<{ success: boolean; data: ITripDetail; message: string }>(
      //     `/trips/${id}`,
      //     {
      //       method: 'GET',
      //     }
      //   );
      //   setTrip(res.data);
      //   setActive(res.data?.status !== 'inactive');
      // } catch (err) {
      //   console.error(err);
      // }
    };

    fetchTrip();
  }, [paramsID]);

  // Fetch user's booking for this trip
  useEffect(() => {
    if (!paramsID || !currentUser?._id) return;

    const fetchUserBooking = async () => {
      try {
        // Fetch user's bookings for this specific trip
        const res = await request<{ success: boolean; data: IUserBooking[]; message: string }>(
          `/bookings/trips/${paramsID}/user`,
          {
            method: 'GET',
            authorization: true,
          }
        );
        
        // Find the first active booking (not cancelled)
        if (res.data && res.data.length > 0) {
          setUserBooking(res.data[0]);
        }
      } catch (err) {
        console.error('Error fetching user booking:', err);
        // It's okay if this fails - user might not have a booking
      }
    };

    fetchUserBooking();
  }, [paramsID, currentUser?._id]);

  const handleDayChange = (event: React.SyntheticEvent, newValue: number, isLocked: any) => {
    if (isLocked) return;
    setSelectedDayIdx(newValue);
  };

  const handlePrevImage = () => {
    if (trip?.files && trip.files.length > 0) {
      setCurrentImageIndex((prev) => (prev === 0 ? trip.files.length - 1 : prev - 1));
    }
  };

  const handleNextImage = () => {
    if (trip?.files && trip.files.length > 0) {
      setCurrentImageIndex((prev) => (prev === trip.files.length - 1 ? 0 : prev + 1));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleCancelBooking = async () => {
    if (!userBooking?._id) {
      alert('Booking ID not found');
      return;
    }

    setIsCancelling(true);
    
    try {
      await request(`/trips/booking/${userBooking._id}/cancel`, {
        method: 'POST',
        authorization: true,
        body: {
          reason: 'Cancelled by user'
        }
      });
      
      setCancelDialogOpen(false);
      alert('Booking cancelled successfully');
      
      // Refresh booking status
      setUserBooking({ ...userBooking, status: 'cancelled' });
    } catch (error: any) {
      console.error('Failed to cancel booking:', error);
      alert(error.message || 'Failed to cancel booking. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelBatch = async () => {
    if (!selectedBatchToCancel || !paramsID) {
      alert('Batch ID not found');
      return;
    }

    setIsCancellingBatch(true);
    
    try {
      const endpoint = type === 'itinerary' 
        ? `/itineraries/${paramsID}/batches/${selectedBatchToCancel}`
        : `/trips/${paramsID}/batches/${selectedBatchToCancel}`;
      
      await request(endpoint, {
        method: 'DELETE',
        authorization: true,
      });
      
      setCancelBatchDialogOpen(false);
      alert('Batch cancelled successfully');
      
      // Refresh trip data
      if (trip?.batches) {
        setTrip({
          ...trip,
          batches: trip.batches.filter(batch => batch._id !== selectedBatchToCancel)
        });
      }
      setSelectedBatchToCancel(null);
    } catch (error: any) {
      console.error('Failed to cancel batch:', error);
      alert(error.message || 'Failed to cancel batch. Please try again.');
    } finally {
      setIsCancellingBatch(false);
    }
  };

  if (!trip) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const { title, description, notes, files, category, pricePerPerson, schedule, location, userId, user: tripUser } = trip;

  const handleChatWithHost = async () => {
    const currentUserId = user?.user?._id || user?.user?.id;
    const hostUserId = tripUser?._id || tripUser?.id;

    if (!currentUserId || !hostUserId) {
      console.warn('Cannot create chat: missing user IDs', { currentUserId, hostUserId });
      return;
    }

    try {
      const chatId = await getOrCreateChat(currentUserId, hostUserId);
      navigate(`/chat/${chatId}`, {
        state: {
          chatId,
          otherUserName: tripUser?._id || tripUser?.id,
          source: 'tripDetail',
        },
      });
    } catch (error) {
      console.error('Error creating chat with host:', error);
      // Optionally add user-facing notification
    }
  };

  const featuredImage = files?.find((f) => f.featured)?.fileURL || files?.[0]?.fileURL || '';

  return (
    <Box sx={{  }}>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 ,backgroundColor: '#fff8f5'}}>
          {/* Image */}
          {featuredImage && (
            <Box sx={{ position: 'relative' }}>
              <Box sx={{ position: 'relative', width: '100%', height: '220px', overflow: 'hidden', borderRadius: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    width: `${(files?.length || 1) * 100}%`,
                    height: '100%',
                    transform: `translateX(-${currentImageIndex * (100 / (files?.length || 1))}% )`,
                    transition: 'transform 0.3s ease-in-out',
                  }}
                >
                  {files?.map((file, index) => (
                    <Box key={index} sx={{ width: '100%', height: '100%' }}>
                      <CardMedia
                        component="img"
                        height="220"
                        image={file.fileURL}
                        alt={`${title} - Image ${index + 1}`}
                        sx={{ objectFit: 'cover', width: '100%', height: '100%' }}
                      />
                    </Box>
                  ))}
                </Box>

                {/* Navigation Arrows */}
                {files && files.length > 1 && (
                  <>
                    <IconButton
                      onClick={handlePrevImage}
                      sx={{
                        position: 'absolute',
                        left: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                        width: 32,
                        height: 32,
                      }}
                    >
                      <ArrowBackIos fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={handleNextImage}
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                        width: 32,
                        height: 32,
                      }}
                    >
                      <ArrowForwardIos fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>

              {/* Image Indicators */}
              {files && files.length > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, gap: 1 }}>
                  {files.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: currentImageIndex === index ? COLORS.primary : 'rgba(0, 0, 0, 0.3)',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: currentImageIndex === index ? COLORS.primary : 'rgba(0, 0, 0, 0.5)',
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
              {/* <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end', mt: 1 }}>
                <IconButton size="small" sx={{ backgroundColor: '#ECE0DA99', borderRadius: 1, borderWidth: 2, borderColor: '#red' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ backgroundColor: '#ECE0DA99', borderRadius: 1, borderWidth: 2, borderColor: '#red' }}>
                  <ShareIcon fontSize="small" />
                </IconButton>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Switch
                    size="medium"
                    checked={active}
                    sx={{
                      '& .MuiSwitch-thumb': {
                        backgroundColor: '#34C759',
                      },
                    }}
                    onChange={(e) => setActive(e.target.checked)}
                  />
                  <Typography variant="caption">Active</Typography>
                </Stack>
              </Box> */}
            </Box>
          )}

          <Box sx={{ p: 2 }}>
            <Typography fontWeight="bold" gutterBottom sx={{fontSize: '16px',color: '#1D1B20'}}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{fontSize: '12px'}}>
              {description}
            </Typography>

            {/* Categories */}
            <Stack direction="row" spacing={1} sx={{ my: '1px' }}>
              {category.map((c) => (
                // <Chip key={c} label={c} size="small" />
                <Typography key={c} variant="body1" color="#49454F" sx={{ fontSize: '14px', fontWeight: '400' }}>{c}</Typography>
              ))}
            </Stack>

            {/* Price & Duration */}
            <Typography sx={{ mt: '1px', color: '#381E72', fontWeight: '500', fontSize: '14px' }}>
              ₹{paiseToRupees(pricePerPerson).toFixed(2)}/person • {schedule.dailySchedule.length} Days
            </Typography>

            {/* Location */}
            {location?.name && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: '1px' }}>
                <LocationIcon fontSize="small" sx={{ color: '#4FC3F7', fontSize: '16px' }} />
                <Typography variant="body2" color="#4FC3F7" sx={{ fontSize: '16px' }}>
                  {location.name}
                </Typography>
              </Stack>
            )}

            {/* Chat with host */}
            <Divider sx={{ my: '8px' }} />
            {tripUser && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={handleChatWithHost}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  },
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                <ChatIcon sx={{ fontSize: '16px', color: '#49454F' }} />
                <Typography variant="body2" color="#757575" sx={{ fontSize: '16px' }}>Chat with Host</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Chip label={tripUser.username} size="small" sx={{ backgroundColor: '#ECE0DA99', borderRadius: 1, borderWidth: 2, borderColor: '#red' }} />
              </Stack>
            )}
            <Divider sx={{ my: '8px' }} />

            {/* Note section */}
            {notes && (
              <Box sx={{ mt: 2 }}>
                <Typography color="#49454F" sx={{ fontSize: '14px' }}>
                  Note-
                </Typography>{' '}
                <Typography color="#49454F" sx={{ fontSize: '14px' }}>
                  {notes}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Available Batches Section */}
        {trip?.batches && trip.batches.length > 0 && (
          <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3, p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <CalendarTodayIcon sx={{ color: COLORS.primary, fontSize: 20 }} />
              <Typography variant="h6" fontWeight="bold">
                Available Dates
              </Typography>
            </Stack>
            <Stack spacing={1.5}>
              {trip.batches.map((batch, index) => (
                <Box
                  key={batch._id || index}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: '#F5F5F5',
                    border: '1px solid #E0E0E0',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#263238' }}>
                        Batch {index + 1}: {formatDate(batch.from)} - {formatDate(batch.to)}
                      </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {(batch.groupSize !== undefined || batch.maxGroupSizeAllowed !== undefined) && (
                        <Chip
                          label={`${batch.groupSize || 0}/${batch.maxGroupSizeAllowed || 0}`}
                          size="small"
                          sx={{
                            backgroundColor: COLORS.primary,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                          }}
                        />
                      )}
                      {/* Show delete button only for trip owner */}
                      {currentUser?.username === tripUser?.username && batch._id && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedBatchToCancel(batch._id || null);
                            setCancelBatchDialogOpen(true);
                          }}
                          sx={{
                            color: 'error.main',
                            '&:hover': {
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {/* User Booking Status Section */}
        {userBooking && (
          <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3, p: 2 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Your Booking
            </Typography>
            
            {/* Booking Status Badge */}
            <Chip
              label={userBooking.status === 'cancelled' ? 'CANCELLED' : userBooking.status.toUpperCase()}
              sx={{
                mb: 2,
                backgroundColor: userBooking.status === 'cancelled' ? '#FFEBEE' : '#E8F5E9',
                color: userBooking.status === 'cancelled' ? '#C62828' : '#2E7D32',
                fontWeight: 'bold',
              }}
            />

            {/* Booking Details */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Travel Dates
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {formatDate(userBooking.batchData.from)} - {formatDate(userBooking.batchData.to)}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Travelers
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {userBooking.quantity} Person{userBooking.quantity > 1 ? 's' : ''}
              </Typography>
            </Box>

            {/* Cancel Button or Cancelled Status */}
            {userBooking.status === 'cancelled' ? (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#FFEBEE', borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CancelIcon sx={{ color: '#C62828' }} />
                  <Typography variant="body2" sx={{ color: '#C62828', fontWeight: 500 }}>
                    This booking has been cancelled
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setCancelDialogOpen(true)}
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                }}
              >
                Cancel Booking
              </Button>
            )}
          </Paper>
        )}

        {/* What you'll do */}
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          What you'll do
        </Typography>

        {/* Day tabs */}
        <Tabs
          value={selectedDayIdx}
          onChange={(event, newValue) => {
            const isLocked = 'locked' in schedule.dailySchedule[newValue];
            handleDayChange(event, newValue, isLocked)
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTabs-flexContainer': {
              gap: 1,
            },
            '& .MuiTab-root': { 
              textTransform: 'none', 
              borderRadius: 3,
              minWidth: 80,
              fontWeight: 'bold',
              fontSize: '0.875rem',
              color: '#263238',
              backgroundColor: 'white',
              border: '1px solid #E0E0E0',
              padding: '8px 16px',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              }
            },
            '& .Mui-selected': { 
              backgroundColor: '#FEB380', 
              color: '#263238 !important',
              border: '1px solid #FEB380',
              '&:hover': {
                backgroundColor: '#FEB380',
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none', // Hide the default indicator
            }
          }}
        >
          {schedule.dailySchedule.map((day, idx) => {
            const isLocked = 'locked' in day;
            return isLocked ? <Tab key={idx} icon={<Lock />} /> : <Tab key={idx} label={`DAY ${idx + 1}`} />;
          })}
        </Tabs>

        <Divider sx={{ my: 2 }} />

        {/* Tasks for selected day */}
        <Stack spacing={2}>
          {schedule.dailySchedule[selectedDayIdx]?.tasks.map((task, idx) => (
            <Paper key={idx} sx={{ p: 1.5, borderRadius: 2, display: 'flex', gap: 2 }} elevation={1}>
              <Avatar
                variant="rounded"
                src={task.file?.fileURL || ''}
                sx={{ width: 64, height: 64, borderRadius: '6px' }}
              />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {task.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {task.description}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
        {currentUser?.username !== tripUser?.username && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              variant="contained"
              onClick={() => {
                // Check if phone is verified before allowing booking
                // if (!isPhoneVerified) {
                //   alert('Please verify your phone number to continue with booking.');
                //   navigate('/phone-verification');
                //   return;
                // }
                // If phone is verified, proceed to checkout
                navigate(`/trips/${paramsID}/checkout`, { state: { tripData: trip,type:type } });
              }}
              sx={{ mt: 2, borderRadius: 12, backgroundColor: '#ff6b35', color: 'white', width: '100%', borderWidth: 2, borderColor: '#red' }}
            >
              Book Now
            </Button>
          </Box>
        )}

      </Container>

      {/* Cancel Booking Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !isCancelling && setCancelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#263238' }}>
          Cancel Booking?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#757575' }}>
            Are you sure you want to cancel this booking? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={isCancelling}
            sx={{
              textTransform: 'none',
              color: '#757575',
              fontWeight: 'bold'
            }}
          >
            No, Keep Booking
          </Button>
          <Button
            onClick={handleCancelBooking}
            variant="contained"
            color="error"
            disabled={isCancelling}
            startIcon={isCancelling ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              px: 3
            }}
          >
            {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Batch Confirmation Dialog */}
      <Dialog
        open={cancelBatchDialogOpen}
        onClose={() => !isCancellingBatch && setCancelBatchDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#263238' }}>
          Delete Batch?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#757575' }}>
            Are you sure you want to delete this batch? All bookings for this batch will be affected. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setCancelBatchDialogOpen(false);
              setSelectedBatchToCancel(null);
            }}
            disabled={isCancellingBatch}
            sx={{
              textTransform: 'none',
              color: '#757575',
              fontWeight: 'bold'
            }}
          >
            No, Keep Batch
          </Button>
          <Button
            onClick={handleCancelBatch}
            variant="contained"
            color="error"
            disabled={isCancellingBatch}
            startIcon={isCancellingBatch ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              px: 3
            }}
          >
            {isCancellingBatch ? 'Deleting...' : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TripDetail;

